export interface GameState {
    readonly currentSection: number;
    readonly currentLevel: number;
    readonly completedLevels: ReadonlyArray<string>;
    readonly lastUpdated: Date;
    readonly maxSections: number;
    readonly maxLevels: number;
}

export class GameStateImpl implements GameState {
    private readonly MIN_SECTION = 1;
    private readonly MIN_LEVEL = 1;

    constructor(
        public readonly currentSection: number = 1,
        public readonly currentLevel: number = 1,
        private readonly _completedLevels: string[] = [],
        public readonly lastUpdated: Date = new Date(),
        public readonly maxSections: number = 7,
        public readonly maxLevels: number = 5
    ) {
        this.validateState();
    }

    private validateState(): void {
        if (this.currentSection < this.MIN_SECTION || this.currentSection > this.maxSections) {
            throw new Error(`Invalid section number: ${this.currentSection}`);
        }
        if (this.currentLevel < this.MIN_LEVEL || this.currentLevel > this.maxLevels) {
            throw new Error(`Invalid level number: ${this.currentLevel}`);
        }
    }

    get completedLevels(): ReadonlyArray<string> {
        return [...this._completedLevels];
    }

    progressToNextLevel(): GameStateImpl {
        if (this.currentLevel >= this.maxLevels) {
            throw new Error('Cannot progress: Already at max level');
        }
        return new GameStateImpl(
            this.currentSection,
            this.currentLevel + 1,
            this._completedLevels,
            new Date(),
            this.maxSections,
            this.maxLevels
        );
    }

    progressToNextSection(): GameStateImpl {
        if (this.currentSection >= this.maxSections) {
            throw new Error('Cannot progress: Already at max section');
        }
        return new GameStateImpl(
            this.currentSection + 1,
            this.MIN_LEVEL,
            this._completedLevels,
            new Date(),
            this.maxSections,
            this.maxLevels
        );
    }

    addCompletedLevel(levelId: string): GameStateImpl {
        if (!levelId) {
            throw new Error('Level ID cannot be empty');
        }
        if (this._completedLevels.includes(levelId)) {
            return this;
        }
        return new GameStateImpl(
            this.currentSection,
            this.currentLevel,
            [...this._completedLevels, levelId],
            new Date(),
            this.maxSections,
            this.maxLevels
        );
    }

    canProgressToNextLevel(): boolean {
        return this.currentLevel < this.maxLevels;
    }

    canProgressToNextSection(): boolean {
        return this.currentSection < this.maxSections;
    }
} 